import { useState, useEffect, useRef } from 'react';
import { Loader2, Copy, Check, AlertTriangle, Terminal } from 'lucide-react';
import { TOKEN_SCOPES, type TokenScope, type TokenWithValue } from '../../hooks/useTokens';
import { type Project } from '../../hooks/useProjects';
import { useToast } from '@/components/ui/toast';
import { CodeBlock } from '@/components/ui/code-block';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

interface CreateTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: {
    project_ids: string[];
    name: string;
    scopes: TokenScope[];
    expires_in_days?: number;
  }) => Promise<TokenWithValue>;
  projects: Project[];
}

const EXPIRY_OPTIONS = [
  { value: 0, label: 'Never expires' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' },
];

export function CreateTokenModal({
  isOpen,
  onClose,
  onCreate,
  projects,
}: CreateTokenModalProps) {
  const [step, setStep] = useState<'form' | 'created'>('form');
  const [name, setName] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<TokenScope[]>(['read']);
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdToken, setCreatedToken] = useState<TokenWithValue | null>(null);
  const [copied, setCopied] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setName('');
      setSelectedProjectIds([]);
      setSelectedScopes(['read']);
      setExpiresInDays(90);
      setError(null);
      setCreatedToken(null);
      setCopied(false);
      setTimeout(() => nameInputRef.current?.focus(), 100);
    }
  }, [isOpen, projects]);

  const handleScopeToggle = (scope: TokenScope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((s) => s !== scope)
        : [...prev, scope]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (selectedProjectIds.length === 0) {
      setError('Select at least one project');
      return;
    }

    if (selectedScopes.length === 0) {
      setError('Select at least one permission');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await onCreate({
        name: name.trim(),
        project_ids: selectedProjectIds,
        scopes: selectedScopes,
        expires_in_days: expiresInDays || undefined,
      });
      setCreatedToken(token);
      setStep('created');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create token');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyTokenToClipboard = async () => {
    if (!createdToken) return;

    try {
      await navigator.clipboard.writeText(createdToken.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy token:', err);
      toast.error('Failed to copy token to clipboard');
    }
  };

  const handleClose = () => {
    if (step === 'form' || createdToken) {
      setCreatedToken(null);
      onClose();
    }
  };

  const selectedProjects = projects.filter((p) => selectedProjectIds.includes(p.id));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        {step === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle>Create Service Token</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="token-name">
                  Token Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  ref={nameInputRef}
                  id="token-name"
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setError(null);
                  }}
                  placeholder="e.g., CI/CD Pipeline, Production Deploy"
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label>
                  Projects <span className="text-destructive">*</span>
                </Label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-border rounded-lg p-2">
                  {projects.map((project) => (
                    <label
                      key={project.id}
                      className="flex items-center gap-3 p-2 hover:bg-accent/50 rounded cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedProjectIds.includes(project.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedProjectIds([...selectedProjectIds, project.id]);
                          } else {
                            setSelectedProjectIds(selectedProjectIds.filter(id => id !== project.id));
                          }
                        }}
                        disabled={isSubmitting}
                      />
                      <span className="text-sm">{project.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Token will have access to secrets in selected projects
                </p>
              </div>

              <div className="space-y-2">
                <Label>
                  Permissions <span className="text-destructive">*</span>
                </Label>
                <div className="space-y-2">
                  {TOKEN_SCOPES.map((scope) => (
                    <label
                      key={scope.value}
                      className="flex items-start gap-3 p-3 border border-border rounded-lg hover:bg-accent/50 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedScopes.includes(scope.value)}
                        onCheckedChange={() => handleScopeToggle(scope.value)}
                        disabled={isSubmitting}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {scope.label}
                        </div>
                        <div className="text-sm text-muted-foreground">{scope.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="token-expiry">Expiration</Label>
                <select
                  id="token-expiry"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(Number(e.target.value))}
                  className="flex h-9 w-full rounded-md border border-border bg-input px-3 text-sm focus-visible:outline-none focus-visible:border-primary/50 focus-visible:ring-2 focus-visible:ring-ring/20"
                  disabled={isSubmitting}
                >
                  {EXPIRY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !name ||
                    selectedProjectIds.length === 0 ||
                    selectedScopes.length === 0
                  }
                >
                  {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Create Token
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success/10 rounded-full">
                <Check className="h-6 w-6 text-success" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Token Created</h2>
            </div>

            <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-warning">
                  Copy your token now
                </p>
                <p className="text-sm text-warning/80 mt-1">
                  This is the only time you will see this token. Store it securely - you
                  won't be able to see it again.
                </p>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Service Token</Label>
              <div className="group relative p-3 bg-muted text-foreground rounded-lg font-mono text-sm break-all pr-12">
                {createdToken?.token}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={copyTokenToClipboard}
                  className={`absolute top-2 right-2 h-8 w-8 ${copied ? 'bg-success/20 text-success' : 'bg-background/80 hover:bg-background'}`}
                  title={copied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2 text-sm text-muted-foreground">
              <p className="truncate">
                <strong className="text-foreground">Name:</strong> {createdToken?.name}
              </p>
              <p className="truncate" title={selectedProjects.map((p) => p.name).join(', ')}>
                <strong className="text-foreground">{selectedProjects.length === 1 ? 'Project' : 'Projects'}:</strong>{' '}
                {selectedProjects.map((p) => p.name).join(', ')}
              </p>
              <p className="truncate">
                <strong className="text-foreground">Permissions:</strong>{' '}
                {createdToken?.scopes
                  .map((s) => TOKEN_SCOPES.find((ts) => ts.value === s)?.label || s)
                  .join(', ')}
              </p>
            </div>

            {/* CLI Usage Examples */}
            <div className="pt-2 min-w-0">
              <div className="flex items-center gap-2 mb-3">
                <Terminal className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Quick Start</Label>
              </div>
              <div className="space-y-3 min-w-0">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground mb-1.5">Set your token:</p>
                  <div className="overflow-x-auto">
                    <CodeBlock compact showPrompt={false} highlightSyntax>
                      {`export KEYENV_TOKEN="${createdToken?.token}"`}
                    </CodeBlock>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Pull secrets:</p>
                  <CodeBlock compact>keyenv pull --env production</CodeBlock>
                </div>
              </div>
            </div>

            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
